// src/components/RecalculationPreviewModal.jsx
// ============================================================================
// Shared Fee Revision — Recalculation Preview Modal
// Extracted from InvoicesPage.jsx for use in both InvoicesPage and TimelinePage.
// ============================================================================
import React, { useState, useMemo } from 'react';
import { X, Copy } from 'lucide-react';
import { getStagePlannedAmount } from '../utils/stagePlannedAmount';

const DEFAULT_GST_PERCENT = 18;
const DEFAULT_TDS_PERCENT = 10;

// ============================================================================
// HELPER: Compute planned_at_new_fee for a single issued invoice (Step 3 rule)
// ============================================================================
export function computePlannedAtNewFee(inv, newFee, project) {
  const basisMode = inv.basisMode || 'percent';
  const pctSnap = inv.invoicePercentSnapshot;
  const stageKey = inv.stageKey;

  // Rule 1: percent or percent_plus_adjustment with snapshot
  if ((basisMode === 'percent' || basisMode === 'percent_plus_adjustment') && pctSnap != null) {
    return { value: newFee * pctSnap / 100, estimated: false };
  }
  // Rule 2: lumpsum — does not scale
  if (basisMode === 'lumpsum') {
    return { value: Number(inv.baseAmount) || 0, estimated: false };
  }
  // Rule 3: adjustment — past adjustments are not revalued
  if (basisMode === 'adjustment') {
    return { value: 0, estimated: false };
  }
  // Rule 4: fallback — snapshot missing, use current stage percent
  if (stageKey && project) {
    const invPct = project.stageTimeline?.[stageKey]?.invoicePercent;
    const allocPct = Number(project.budgetAllocation?.[stageKey]) || 0;
    const pct = invPct != null ? Number(invPct) : allocPct;
    return { value: newFee * pct / 100, estimated: true };
  }
  // No stage key and no snapshot — cannot revalue
  return { value: Number(inv.baseAmount) || 0, estimated: true };
}

// ============================================================================
// HELPER: Run the 7-step recalculation logic
// ============================================================================
export function computeRecalcBreakdown(issuedInvoices, newFee, project, stageKey) {
  const issuedLines = issuedInvoices.map(inv => {
    const issuedBase = Number(inv.baseAmount) || 0;
    const { value: plannedAtNewFee, estimated } = computePlannedAtNewFee(inv, newFee, project);
    return {
      id: inv.id,
      invoiceNumber: inv.invoiceNumber || `#${inv.id}`,
      stageKey: inv.stageKey,
      basisMode: inv.basisMode || 'percent',
      invoicePercentSnapshot: inv.invoicePercentSnapshot,
      issuedBase,
      plannedAtNewFee,
      delta: plannedAtNewFee - issuedBase,
      estimated,
    };
  });
  const issuedTotalBase = issuedLines.reduce((s, l) => s + l.issuedBase, 0);
  const issuedTotalAtNewFee = issuedLines.reduce((s, l) => s + l.plannedAtNewFee, 0);
  const adjustmentDue = issuedTotalAtNewFee - issuedTotalBase;
  const invPct = project?.stageTimeline?.[stageKey]?.invoicePercent;
  const allocPct = Number(project?.budgetAllocation?.[stageKey]) || 0;
  const currentStagePercent = invPct != null ? Number(invPct) : allocPct;
  // A lump sum stage bills its lump sum whatever the fee: lump sums do not
  // scale on a revision (Rule 2), and its % may be a note against a different
  // base (RF's AD and ID fees), so % × new fee would set the wrong base.
  const currentStagePlanned = getStagePlannedAmount(project, stageKey, newFee);

  return { issuedLines, issuedTotalBase, issuedTotalAtNewFee, adjustmentDue, currentStagePlanned, currentStagePercent };
}

// ============================================================================
// HELPER: Generate recalculation explanation text (7-step version)
// ============================================================================
export function generateExplanationText({
  projectName, newFee, breakdown, stageKey, stageName,
  approach, gstPct, tdsPct, invoiceBase,
}) {
  const fmtN = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  const { issuedLines, issuedTotalBase, issuedTotalAtNewFee, adjustmentDue, currentStagePlanned } = breakdown;
  const lines = [];

  lines.push(`Fee Revision — Adjustment Preview — ${projectName}`);
  lines.push('─'.repeat(60));
  lines.push('');
  lines.push(`Current total fee: ₹${fmtN(newFee)}`);
  lines.push('');

  // Issued invoices table
  lines.push('Already-issued invoices (frozen facts):');
  lines.push(`${'Invoice'.padEnd(16)} ${'Stage'.padEnd(14)} ${'Mode'.padEnd(12)} ${'%snap'.padStart(6)} ${'Issued'.padStart(14)} ${'At new fee'.padStart(14)} ${'Delta'.padStart(14)}`);
  lines.push('─'.repeat(90));
  issuedLines.forEach(l => {
    const pctStr = l.invoicePercentSnapshot != null ? `${l.invoicePercentSnapshot}%` : '—';
    lines.push(
      `${l.invoiceNumber.padEnd(16)} ${(l.stageKey || '—').padEnd(14)} ${l.basisMode.padEnd(12)} ${pctStr.padStart(6)} ₹${fmtN(l.issuedBase).padStart(13)} ₹${fmtN(l.plannedAtNewFee).padStart(13)} ${(l.delta >= 0 ? '+' : '') + '₹' + fmtN(l.delta).padStart(12)}${l.estimated ? ' [!]est' : ''}`
    );
  });
  lines.push('─'.repeat(90));
  lines.push(
    `${'TOTAL'.padEnd(16)} ${''.padEnd(14)} ${''.padEnd(12)} ${''.padStart(6)} ₹${fmtN(issuedTotalBase).padStart(13)} ₹${fmtN(issuedTotalAtNewFee).padStart(13)} ${(adjustmentDue >= 0 ? '+' : '') + '₹' + fmtN(adjustmentDue).padStart(12)}`
  );
  lines.push('');

  // Step summary
  lines.push('Step 1 — Total already issued (facts):  ₹' + fmtN(issuedTotalBase));
  lines.push('Step 2 — Current fee (new model):       ₹' + fmtN(newFee));
  lines.push('Step 3 — Issued revalued at new fee:    ₹' + fmtN(issuedTotalAtNewFee));
  lines.push(`Step 4 — Adjustment due (3 − 1):        ${adjustmentDue >= 0 ? '+' : ''}₹${fmtN(adjustmentDue)}`);
  lines.push(`Step 5 — Current stage (${stageName || stageKey}) at new fee: ₹${fmtN(currentStagePlanned)}`);
  lines.push('');

  // This invoice
  lines.push('This invoice:');
  const adj = approach === 'roll_in' ? adjustmentDue : 0;
  const totalBase = currentStagePlanned + adj;
  if (approach === 'roll_in' && adj !== 0) {
    lines.push(`  Stage planned:       ₹${fmtN(currentStagePlanned)}`);
    lines.push(`  Adjustment rolled in: ${adj >= 0 ? '+' : ''}₹${fmtN(adj)}`);
    lines.push(`  Base amount:          ₹${fmtN(totalBase)}`);
  } else {
    lines.push(`  Base amount:          ₹${fmtN(currentStagePlanned)}`);
    if (approach === 'separate' && adjustmentDue !== 0) {
      lines.push(`  (Separate adjustment invoice: ${adjustmentDue >= 0 ? '+' : ''}₹${fmtN(adjustmentDue)})`);
    }
  }
  const g = Number(gstPct) || 0;
  const t = Number(tdsPct) || 0;
  const effectiveBase = approach === 'roll_in' ? totalBase : currentStagePlanned;
  const gstAmt = effectiveBase * g / 100;
  const tdsAmt = effectiveBase * t / 100;
  const gross = effectiveBase + gstAmt;
  const net = gross - tdsAmt;
  lines.push(`  GST (${g}% on base):   +₹${fmtN(gstAmt)}`);
  lines.push(`  Gross:                ₹${fmtN(gross)}`);
  lines.push(`  TDS (${t}% on base):   −₹${fmtN(tdsAmt)}`);
  lines.push(`  Net receivable:       ₹${fmtN(net)}`);
  lines.push('');
  lines.push(`Approach: ${approach === 'separate' ? 'Adjustment issued as a separate invoice.' : 'Adjustment rolled into this invoice (adjustment_amount stored separately for transparency).'}`);

  return lines.join('\n');
}

// ============================================================================
// RecalculationPreviewModal Component
// ============================================================================
export default function RecalculationPreviewModal({
  revision, project, stages, stageTimeline, budgetAllocation,
  invoicesHook, currentInvoice, fmtCurr, formatMoney, getCurrentTotalFee,
  onIssue, onIssueWithSeparateAdjustment, onClose,
  mode = 'issue', // 'issue' | 'create'
}) {
  const [approach, setApproach] = useState('roll_in');
  const [issuing, setIssuing] = useState(false);
  const [copied, setCopied] = useState(false);
  const isCreateMode = mode === 'create';
  // Invoice amounts are shown exactly: the default formatMoney rounds to the
  // nearest 100 above 1,000, which made a 5,090 stage read as 5,100 here.
  const fmtCC = (a) => formatMoney ? formatMoney(a, project?.contractCurrency || 'INR', { exact: true }) : fmtCurr(a);

  // Step 2: current fee
  const newFee = getCurrentTotalFee(project);
  const stageKey = currentInvoice?.stageKey;
  const stageName = (stages || []).find(s => s.key === stageKey)?.name || stageKey || '—';

  // Steps 1-5: compute the full breakdown from all issued invoices
  const issuedInvoices = useMemo(() => {
    return invoicesHook?.getIssuedInvoices?.() || [];
  }, [invoicesHook]);

  const breakdown = useMemo(() => {
    return computeRecalcBreakdown(issuedInvoices, newFee, project, stageKey);
  }, [issuedInvoices, newFee, project, stageKey]);

  const { issuedLines, issuedTotalBase, issuedTotalAtNewFee, adjustmentDue, currentStagePlanned, currentStagePercent } = breakdown;

  // Explanation text — updates when approach changes
  const explanationText = useMemo(() => generateExplanationText({
    projectName: project?.name || '',
    newFee,
    breakdown,
    stageKey,
    stageName,
    approach,
    gstPct: DEFAULT_GST_PERCENT,
    tdsPct: DEFAULT_TDS_PERCENT,
    invoiceBase: approach === 'roll_in' ? currentStagePlanned + adjustmentDue : currentStagePlanned,
  }), [project, newFee, breakdown, stageKey, stageName, approach, currentStagePlanned, adjustmentDue]);

  const handleCopy = () => {
    navigator.clipboard.writeText(explanationText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleConfirm = async () => {
    setIssuing(true);
    if (approach === 'separate') {
      await onIssueWithSeparateAdjustment(adjustmentDue, explanationText, revision);
    } else {
      await onIssue(adjustmentDue, explanationText, revision);
    }
    setIssuing(false);
  };

  const cellStyle = { padding: '4px 6px', fontSize: '10px', borderBottom: '1px solid var(--brio-border)' };
  const hasEstimated = issuedLines.some(l => l.estimated);

  // Plain-language explanation of the calculation method
  const oldFee = revision?.oldTotalFee;
  const feeDirection = adjustmentDue > 0 ? 'increased' : adjustmentDue < 0 ? 'decreased' : 'changed';
  const feeDirectionNoun = adjustmentDue > 0 ? 'underbilled' : adjustmentDue < 0 ? 'overbilled' : 'unchanged';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1001, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--brio-white, #fff)', border: '1px solid var(--brio-border)', width: '100%', maxWidth: '720px', maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div className="brio-flex brio-justify-between brio-items-center" style={{ padding: '12px 16px', borderBottom: '1px solid var(--brio-border)', background: 'var(--brio-gold-light)' }}>
          <span className="brio-font-bold" style={{ fontSize: '13px' }}>[!] Fee Revision — Adjustment Preview</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X className="w-4 h-4" /></button>
        </div>

        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

          {/* Plain-language explanation */}
          <div style={{ padding: '10px 12px', background: 'var(--brio-gold-light)', border: '1px solid var(--brio-gold, #9F902A)', borderRadius: '4px' }}>
            <div className="brio-text-s" style={{ lineHeight: '1.5' }}>
              The project fee {feeDirection} from {oldFee != null ? fmtCC(oldFee) : '—'} to {fmtCC(newFee)}
              {revision?.effectiveDate ? ` (effective ${revision.effectiveDate})` : ''}.
              {issuedLines.length > 0
                ? ` ${issuedLines.length} invoice${issuedLines.length > 1 ? 's were' : ' was'} already issued at the old fee. The table below revalues each at the new fee to calculate the difference.`
                : ' No invoices have been issued yet, so no adjustment is needed.'
              }
              {adjustmentDue !== 0 && (
                <> The total {feeDirectionNoun} amount is <strong>{adjustmentDue >= 0 ? '+' : ''}{fmtCC(adjustmentDue)}</strong> — calculated as: sum of invoices revalued at new fee ({fmtCC(issuedTotalAtNewFee)}) minus sum actually invoiced ({fmtCC(issuedTotalBase)}).</>
              )}
            </div>
            <div className="brio-text-xs brio-text-grey" style={{ marginTop: '6px' }}>
              Formula: Adjustment = Σ(each issued invoice revalued at new fee) − Σ(each issued invoice base amount)
            </div>
          </div>

          {/* Fee & stage summary */}
          <div style={{ padding: '10px 12px', background: 'var(--brio-grey-light)', border: '1px solid var(--brio-border)' }}>
            <div className="brio-flex brio-justify-between brio-text-s"><span className="brio-text-grey">Current Total Fee</span><span className="brio-font-bold">{fmtCC(newFee)}</span></div>
            <div className="brio-flex brio-justify-between brio-text-s"><span className="brio-text-grey">Stage: {stageName} ({currentStagePercent}%)</span><span>{fmtCC(currentStagePlanned)}</span></div>
            {revision.reason && (
              <div className="brio-text-xs brio-text-grey" style={{ marginTop: '4px' }}>Last revision reason: {revision.reason}</div>
            )}
          </div>

          {/* Issued invoices table (invoice-level, Step 1-3) */}
          {issuedLines.length > 0 && (
            <div>
              <div className="brio-text-xs brio-font-medium" style={{ marginBottom: '4px' }}>
                Issued Invoices — Revaluation at Current Fee
                {hasEstimated && <span style={{ color: 'var(--brio-gold)', marginLeft: '6px' }}>[!] some rows estimated (missing % snapshot)</span>}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                <thead>
                  <tr style={{ background: 'var(--brio-grey-light)', borderBottom: '2px solid var(--brio-border)' }}>
                    <th style={{ ...cellStyle, textAlign: 'left' }}>Invoice</th>
                    <th style={{ ...cellStyle, textAlign: 'left' }}>Stage</th>
                    <th style={{ ...cellStyle, textAlign: 'center' }}>Mode</th>
                    <th style={{ ...cellStyle, textAlign: 'right' }}>% snap</th>
                    <th style={{ ...cellStyle, textAlign: 'right' }}>Issued Base</th>
                    <th style={{ ...cellStyle, textAlign: 'right' }}>At New Fee</th>
                    <th style={{ ...cellStyle, textAlign: 'right' }}>Delta</th>
                  </tr>
                </thead>
                <tbody>
                  {issuedLines.map(l => (
                    <tr key={l.id} style={l.estimated ? { background: 'var(--brio-gold-light)' } : undefined}>
                      <td style={{ ...cellStyle, fontFamily: 'monospace' }}>{l.invoiceNumber}{l.estimated && ' [!]'}</td>
                      <td style={cellStyle}>{l.stageKey || '—'}</td>
                      <td style={{ ...cellStyle, textAlign: 'center' }}>{l.basisMode}</td>
                      <td style={{ ...cellStyle, textAlign: 'right' }}>{l.invoicePercentSnapshot != null ? `${l.invoicePercentSnapshot}%` : '—'}</td>
                      <td style={{ ...cellStyle, textAlign: 'right' }}>{fmtCC(l.issuedBase)}</td>
                      <td style={{ ...cellStyle, textAlign: 'right' }}>{fmtCC(l.plannedAtNewFee)}</td>
                      <td style={{ ...cellStyle, textAlign: 'right', color: l.delta >= 0 ? 'var(--brio-green, #16a34a)' : 'var(--brio-red)' }}>
                        {l.delta >= 0 ? '+' : ''}{fmtCC(l.delta)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: 'var(--brio-grey-light)', fontWeight: 600 }}>
                    <td style={cellStyle} colSpan={4}>TOTAL</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{fmtCC(issuedTotalBase)}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{fmtCC(issuedTotalAtNewFee)}</td>
                    <td style={{ ...cellStyle, textAlign: 'right', color: adjustmentDue >= 0 ? 'var(--brio-green, #16a34a)' : 'var(--brio-red)' }}>
                      {adjustmentDue >= 0 ? '+' : ''}{fmtCC(adjustmentDue)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Step 4: adjustment summary */}
          <div style={{ padding: '10px 12px', border: `2px solid ${adjustmentDue === 0 ? 'var(--brio-border)' : adjustmentDue > 0 ? 'var(--brio-green, #16a34a)' : 'var(--brio-red)'}`, background: adjustmentDue === 0 ? 'var(--brio-grey-light)' : undefined }}>
            <div className="brio-text-xs brio-font-medium" style={{ marginBottom: '6px' }}>Adjustment Calculation</div>
            <div className="brio-flex brio-justify-between brio-text-s"><span className="brio-text-grey">Total already issued (facts)</span><span>{fmtCC(issuedTotalBase)}</span></div>
            <div className="brio-flex brio-justify-between brio-text-s"><span className="brio-text-grey">Value at revised fee (model)</span><span>{fmtCC(issuedTotalAtNewFee)}</span></div>
            <div className="brio-flex brio-justify-between brio-text-s brio-font-bold" style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid var(--brio-border)', color: adjustmentDue >= 0 ? 'var(--brio-green, #16a34a)' : 'var(--brio-red)' }}>
              <span>Adjustment due</span>
              <span>{adjustmentDue >= 0 ? '+' : ''}{fmtCC(adjustmentDue)}</span>
            </div>
          </div>

          {/* This invoice */}
          <div style={{ padding: '10px 12px', background: 'var(--brio-grey-light)', border: '1px solid var(--brio-border)' }}>
            <div className="brio-text-xs brio-font-medium" style={{ marginBottom: '6px' }}>This Invoice — {stageName}</div>
            <div className="brio-flex brio-justify-between brio-text-s"><span className="brio-text-grey">Stage planned at current fee</span><span>{fmtCC(currentStagePlanned)}</span></div>
            {approach === 'roll_in' && adjustmentDue !== 0 && (
              <>
                <div className="brio-flex brio-justify-between brio-text-s" style={{ color: adjustmentDue >= 0 ? 'var(--brio-green, #16a34a)' : 'var(--brio-red)' }}>
                  <span>+ Adjustment rolled in</span>
                  <span>{adjustmentDue >= 0 ? '+' : ''}{fmtCC(adjustmentDue)}</span>
                </div>
                <div className="brio-flex brio-justify-between brio-text-s brio-font-bold" style={{ marginTop: '2px', paddingTop: '2px', borderTop: '1px solid var(--brio-border)' }}>
                  <span>Invoice base</span><span>{fmtCC(currentStagePlanned + adjustmentDue)}</span>
                </div>
              </>
            )}
          </div>

          {/* Step 6: Approach selection */}
          {adjustmentDue !== 0 && (
            <div style={{ padding: '10px 12px', border: '1px solid var(--brio-border)' }}>
              <div className="brio-text-xs brio-font-medium" style={{ marginBottom: '6px' }}>Adjustment Approach</div>
              <label className="brio-flex brio-items-center brio-gap-xs brio-text-s" style={{ cursor: 'pointer', marginBottom: '4px' }}>
                <input type="radio" name="approach" checked={approach === 'roll_in'} onChange={() => setApproach('roll_in')} />
                <span><strong>Roll into this invoice</strong> — base = {fmtCC(currentStagePlanned)} + {adjustmentDue >= 0 ? '+' : ''}{fmtCC(adjustmentDue)} = {fmtCC(currentStagePlanned + adjustmentDue)}</span>
              </label>
              <label className="brio-flex brio-items-center brio-gap-xs brio-text-s" style={{ cursor: 'pointer' }}>
                <input type="radio" name="approach" checked={approach === 'separate'} onChange={() => setApproach('separate')} />
                <span><strong>Separate adjustment invoice</strong> — stage at {fmtCC(currentStagePlanned)} + separate adjustment of {adjustmentDue >= 0 ? '+' : ''}{fmtCC(adjustmentDue)}</span>
              </label>
            </div>
          )}

          {/* Copy explanation */}
          <div>
            <button onClick={handleCopy} className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs" style={{ padding: '6px 12px', fontSize: '11px' }}>
              <Copy className="w-3 h-3" /> {copied ? 'Copied!' : 'Copy Explanation for Client'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="brio-flex brio-justify-end brio-gap-xs" style={{ padding: '12px 16px', borderTop: '1px solid var(--brio-border)' }}>
          <button onClick={onClose} className="brio-btn brio-btn-secondary" style={{ padding: '6px 14px', fontSize: '11px' }}>Cancel</button>
          <button onClick={handleConfirm} disabled={issuing} className="brio-btn brio-btn-primary" style={{ padding: '6px 14px', fontSize: '11px', opacity: issuing ? 0.5 : 1 }}>
            {issuing ? (isCreateMode ? 'Creating…' : 'Issuing…') : (isCreateMode ? 'Create Draft' : 'Issue Invoice')}
          </button>
        </div>
      </div>
    </div>
  );
}
